class StoreNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    this.menuButton = this.querySelector('[data-nav-action="menu"]');
    this.dialog.id = `navigation-menu-${this.dataset.sectionId}`;
    this.menuButton.setAttribute('aria-controls', this.dialog.id);
    this.menuButton.setAttribute('aria-expanded', 'false');
    this.dropdowns = [...this.querySelectorAll('[data-nav-dropdown]')].map((button) => {
      const panel = this.querySelector(`[data-nav-panel="${button.dataset.navDropdown}"]`).parentElement;
      panel.id = `navigation-${this.dataset.sectionId}-${button.dataset.navDropdown}`;
      button.setAttribute('aria-controls', panel.id);
      return { button, panel };
    });
    this.querySelectorAll('[data-nav-type="accordion-item"]').forEach((item, index) => {
      const button = item.querySelector('button');
      const panel = item.querySelector('[data-nav-type="accordion-content"]');
      panel.id = `navigation-accordion-${this.dataset.sectionId}-${index}`;
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const open = panel.hidden;
        panel.hidden = !open;
        item.dataset.state = open ? 'open' : 'closed';
        button.setAttribute('aria-expanded', String(open));
      }, { signal });
    });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-nav-dropdown]');
      if (button) {
        const open = button.getAttribute('aria-expanded') !== 'true';
        this.closeDropdowns();
        if (open) this.openDropdown(button);
      }
      const action = event.target.closest('[data-nav-action]')?.dataset.navAction;
      if (action === 'menu') {
        this.closeDropdowns();
        this.restoreMenuFocus = true;
        this.dialog.showModal();
        this.querySelector('[data-nav-action="close"]').focus();
        this.menuButton.setAttribute('aria-expanded', 'true');
      }
      if (action === 'close') this.dialog.close();
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
      const card = event.target.closest('[data-nav-href]');
      if (card && !event.target.closest('a, button, select')) card.querySelector('a')?.click();
    }, { signal });
    // Close the menu before the theme or cart app handles a cart link.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-nav-cart]') && this.dialog.open) {
        this.restoreMenuFocus = false;
        this.dialog.close();
      }
    }, { capture: true, signal });
    this.dialog.addEventListener('close', () => {
      this.menuButton.setAttribute('aria-expanded', 'false');
      if (this.restoreMenuFocus && this.menuButton.getClientRects().length) this.menuButton.focus();
    }, { signal });
    this.addEventListener('change', (event) => {
      if (event.target.matches('select[name="language_code"]')) event.target.form.requestSubmit();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (this.dialog.open && event.key === 'Tab') {
        const focusable = [...this.dialog.querySelectorAll('a[href], button, select, [tabindex="0"]')]
          .filter((element) => !element.disabled && element.getClientRects().length);
        const target = event.shiftKey ? focusable.at(-1) : focusable[0];
        const boundary = event.shiftKey ? focusable[0] : focusable.at(-1);
        if (document.activeElement === boundary) {
          event.preventDefault();
          target.focus();
        }
      }
      const button = event.target.closest('[data-nav-dropdown]');
      if (button && event.key === 'ArrowDown') {
        event.preventDefault();
        this.closeDropdowns();
        const panel = this.openDropdown(button);
        panel.querySelector('a, button')?.focus();
      }
      if (event.key === 'Escape' && !this.dialog.open) {
        const active = this.dropdowns.find(({ panel }) => !panel.hidden);
        if (active) {
          event.preventDefault();
          this.closeDropdowns();
          active.button.focus();
        }
      }
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeDropdowns();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeDropdowns();
    }, { signal });
    this.media = matchMedia(`(max-width: ${this.dataset.breakpoint}px)`);
    this.media.addEventListener('change', () => {
      this.closeDropdowns();
      if (this.dialog.open) this.dialog.close();
    }, { signal });
    this.cards = [...this.querySelectorAll('.ifFS8Ca882QBFjFww, .iERIN9u7Ei8foK4fx, .iqCD9CPkkH0lIhJTw')];
    this.observer = new ResizeObserver(() => this.sizePanels());
    this.observer.observe(this.querySelector('.iAvaq5NvBZgzeP7zY'));
    this.sizePanels();
  }

  openDropdown(button) {
    const { panel } = this.dropdowns.find((entry) => entry.button === button);
    panel.hidden = false;
    button.dataset.navState = 'active';
    button.setAttribute('aria-expanded', 'true');
    this.sizePanels();
    return panel;
  }

  closeDropdowns() {
    this.dropdowns.forEach(({ button, panel }) => {
      panel.hidden = true;
      button.dataset.navState = 'inactive';
      button.setAttribute('aria-expanded', 'false');
    });
  }

  sizePanels() {
    const bottom = this.querySelector('.iAvaq5NvBZgzeP7zY').getBoundingClientRect().bottom;
    this.style.setProperty('--navigation-bottom', `${bottom}px`);
    this.cards.forEach((card) => {
      card.hidden = false;
      if (!card.getClientRects().length) return;
      const style = getComputedStyle(card);
      card.hidden = card.getBoundingClientRect().width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) < 200;
    });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.observer.disconnect();
    if (this.dialog.open) this.dialog.close();
  }
}

if (!customElements.get('store-navigation')) customElements.define('store-navigation', StoreNavigation);